import React from "react";
import { useField, useFormikContext } from "formik";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import ErrorField from "../errorField";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "../../util";
import { CurrencyInputFieldConfig } from "./types";
import CurrencyInput from "shared/form/core/input/moneyInput";
import { getFormattedNumberOrCurrency, getLocaleId, isEmpty } from "shared/utils";
import FormOverwriteField from "../overwriteField";
import { useFormContextData } from "shared/form/context";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";

function FormMoneyField(props: CurrencyInputFieldConfig) {
  const formContextData = useFormContextData();
  const { setFieldTouched, setFieldValue, validateForm, validateOnChange } = useFormikContext();
  const [{ value, onBlur }, meta] = useField({
    name: props.name,
    validate: props.validate
  });
  const [editing, setEditing] = React.useState(false);
  const [rawValue, setRawValue] = React.useState(() => value);
  const localeId = React.useMemo(() => props.localeId || getLocaleId(), [props.localeId]);

  const changeHandler = React.useCallback(
    async value => {
      setRawValue(value);
      //if using comma as separator then replace it with .
      const parsedValue = value?.replace(",", ".") || value;
      await setFieldValue(props.name, parsedValue, false);
      if (!meta.touched) {
        await setFieldTouched(props.name, true, false);
      }
      validateOnChange && (await validateForm());
    },
    [setFieldValue, setFieldTouched, validateForm, validateOnChange, props.name, meta.touched]
  );

  const blurHandler = React.useCallback(
    async e => {
      if (props.doNotRunOnBlurRef?.current) return;
      await onBlur(e);
      if (props.disableSymbolWhileEditing) {
        setEditing(false);
      }
    },
    [props.doNotRunOnBlurRef, props.disableSymbolWhileEditing, onBlur]
  );

  const focusHandler = React.useCallback(
    e => {
      props.onFocus?.(e);
      if (props.disableSymbolWhileEditing) {
        setEditing(true);
      }
    },
    [props]
  );

  const getFormattedFieldValue = React.useCallback(
    ({ value }) => {
      return getFormattedNumberOrCurrency({
        currencyCode: props.currencyCode,
        localeId: props.localeId,
        value,
        formatValue: props.formatValue
      });
    },
    [props.currencyCode, props.formatValue, props.localeId]
  );

  const displayValue = React.useMemo(() => {
    if (rawValue && !isEmpty(value)) {
      //Added check on value to prevent showing previous value on field reset.
      return rawValue;
    }
    return value;
  }, [rawValue, value]);

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
        <FormLabel
          label={props.label}
          required={props.required}
          additionalLabel={props.additionalLabel}
          infoLabel={props.infoLabel}
        />
        {formContextData.isReadOnly ? (
          getValueForReadOnlyFieldForStaticForm({
            renderType: FieldRenderType.MONEY,
            value: getFormattedFieldValue({ value })
          })
        ) : (
          <CurrencyInput
            name={props.name}
            value={displayValue}
            formValue={value}
            placeholder={props.placeholder}
            onChange={changeHandler}
            onBlur={blurHandler}
            type={props.type}
            disabled={props.disabled}
            className={props.className}
            icon={props.icon}
            hasError={meta.touched && meta.error}
            autoComplete={props.autoComplete}
            autoFocus={props.autoFocus}
            switchIconPosition={props.switchIconPosition}
            onFocus={focusHandler}
            locale={localeId}
            currencyCode={editing ? undefined : props.currencyCode}
            suffix={editing ? undefined : props.suffix}
            disableGroupSeparators={!props.formatValue}
          />
        )}
        <ErrorField meta={meta} />
        {props.enableFieldReset && <FieldResetWhenRemoved name={props.name} />}
        {!isEmpty(props.conflictValue) && (
          <FormOverwriteField
            forFieldName={props.name}
            disabled={props.disabled}
            customDisplayValue={getFormattedFieldValue}
          />
        )}
      </FormGroup>
    </React.Fragment>
  );
}

export default FormMoneyField;
